import { midiToHz } from './timing';

/**
 * Synthesised UI sounds (plan doc 10). No assets: every sound is a few
 * oscillators and envelopes, so they are instant and weigh nothing. The
 * `correct`/`combo` sounds are key-aware through `params.rootMidi`.
 */

export type UiSoundId =
  | 'tap'
  | 'pop'
  | 'correct'
  | 'wrong'
  | 'combo'
  | 'xpTick'
  | 'chest'
  | 'levelUp'
  | 'streak'
  | 'promote'
  | 'unlock'
  | 'whoosh';

export interface UiSoundParams {
  /** Tonic of the current exercise; defaults to C4. */
  rootMidi?: number;
  minor?: boolean;
  /** Combo count for `combo`, option index for `pop`, tick index for `xpTick`. */
  index?: number;
  /** Absolute audio time; defaults to now. */
  at?: number;
}

interface Tone {
  type: OscillatorType;
  hz: number;
  /** Seconds after `at`. */
  start: number;
  /** Seconds. */
  duration: number;
  gain: number;
  /** Optional glide target in Hz. */
  glideTo?: number;
  lowpassHz?: number;
  attack?: number;
}

export class UiSynth {
  constructor(
    private readonly ctx: BaseAudioContext,
    private readonly destination: AudioNode,
  ) {}

  play(id: UiSoundId, params: UiSoundParams = {}): void {
    const at = params.at ?? this.ctx.currentTime;
    const root = params.rootMidi ?? 60;
    const third = params.minor === true ? 3 : 4;
    switch (id) {
      case 'tap':
        this.tones([{ type: 'sine', hz: 1200, start: 0, duration: 0.025, gain: 0.15 }], at);
        break;
      case 'pop': {
        const shift = ((params.index ?? 0) % 4) * 40;
        this.tones(
          [
            {
              type: 'sine',
              hz: 600 + shift,
              glideTo: 300 + shift,
              start: 0,
              duration: 0.06,
              gain: 0.18,
            },
          ],
          at,
        );
        break;
      }
      case 'correct':
        this.arpeggio(root + 12, [0, third, 7], at, 0.045, 0.14, 0.16);
        break;
      case 'combo': {
        const combo = params.index ?? 5;
        const transpose = combo >= 15 ? 12 : combo >= 10 ? 7 : 5;
        const degrees = combo >= 10 ? [0, third, 7, 12] : [0, third, 7];
        this.arpeggio(root + 12 + transpose, degrees, at, 0.045, 0.16, 0.18);
        break;
      }
      case 'wrong':
        this.tones(
          [
            {
              type: 'sawtooth',
              hz: 110,
              glideTo: 95,
              start: 0,
              duration: 0.18,
              gain: 0.2,
              lowpassHz: 600,
            },
          ],
          at,
        );
        this.noise(at, 0.03, 0.08, 3000);
        break;
      case 'xpTick': {
        const step = Math.min(40, params.index ?? 0);
        this.tones(
          [{ type: 'sine', hz: 880 * 1.02 ** step, start: 0, duration: 0.03, gain: 0.12 }],
          at,
        );
        break;
      }
      case 'chest':
        this.noise(at, 0.4, 0.12, 1200, 400);
        this.sparkle(root, at + 0.35);
        break;
      case 'levelUp':
        this.fanfare(root, at, third);
        break;
      case 'streak':
        this.noise(at, 0.35, 0.16, 200, 4000);
        this.arpeggio(root + 12, [0, third, 7], at + 0.2, 0, 0.6, 0.14, 'triangle', true);
        break;
      case 'promote':
        this.arpeggio(root + 12, [0, 2, 4, 7, 9], at, 0.09, 0.12, 0.16);
        this.arpeggio(root + 12, [0, 4, 7, 12], at + 0.5, 0, 0.7, 0.12, 'triangle');
        break;
      case 'unlock':
        this.tones(
          [
            { type: 'sine', hz: midiToHz(root + 24), start: 0, duration: 0.5, gain: 0.18 },
            { type: 'sine', hz: midiToHz(root + 24) * 2.4, start: 0, duration: 0.35, gain: 0.07 },
          ],
          at,
        );
        break;
      case 'whoosh':
        this.noise(at, 0.25, 0.05, 300, 2500);
        break;
      default:
        break;
    }
  }

  /* ----------------------------------------------------------- building */

  private tones(tones: readonly Tone[], at: number): void {
    for (const tone of tones) {
      const osc = this.ctx.createOscillator();
      osc.type = tone.type;
      const start = at + tone.start;
      osc.frequency.setValueAtTime(tone.hz, start);
      if (tone.glideTo !== undefined) {
        osc.frequency.exponentialRampToValueAtTime(
          Math.max(1, tone.glideTo),
          start + tone.duration,
        );
      }
      const gain = this.ctx.createGain();
      const attack = tone.attack ?? 0.004;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(tone.gain, start + attack);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + tone.duration);
      let node: AudioNode = osc;
      if (tone.lowpassHz !== undefined) {
        const filter = this.ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(tone.lowpassHz, start);
        node.connect(filter);
        node = filter;
      }
      node.connect(gain);
      gain.connect(this.destination);
      osc.start(start);
      osc.stop(start + tone.duration + 0.02);
    }
  }

  private arpeggio(
    rootMidi: number,
    degrees: readonly number[],
    at: number,
    stagger: number,
    duration: number,
    gain: number,
    type: OscillatorType = 'triangle',
    tremolo = false,
  ): void {
    degrees.forEach((degree, i) => {
      this.tones(
        [
          {
            type,
            hz: midiToHz(rootMidi + degree),
            start: i * stagger,
            duration,
            gain,
            lowpassHz: 4000,
            ...(tremolo ? { attack: 0.03 } : {}),
          },
        ],
        at,
      );
    });
  }

  private fanfare(root: number, at: number, third: number): void {
    const chords = [
      [0, third, 7],
      [5, 9, 12],
      [7, 11, 14],
      [12, 12 + third, 19],
    ];
    chords.forEach((chord, i) => {
      const last = i === chords.length - 1;
      chord.forEach((degree) => {
        this.tones(
          [
            {
              type: 'square',
              hz: midiToHz(root + degree),
              start: i * 0.12,
              duration: last ? 0.6 : 0.11,
              gain: 0.05,
              lowpassHz: 1800,
            },
            ...(last
              ? [
                  {
                    type: 'square' as const,
                    hz: midiToHz(root + degree) * 1.004,
                    start: i * 0.12,
                    duration: 0.6,
                    gain: 0.03,
                    lowpassHz: 1800,
                  },
                ]
              : []),
          ],
          at,
        );
      });
    });
  }

  private sparkle(root: number, at: number): void {
    const degrees = [0, 5, 7, 12, 17, 19, 24, 29, 31, 36, 41, 43];
    degrees.forEach((degree, i) => {
      this.tones(
        [
          {
            type: 'sine',
            hz: midiToHz(root + 12 + degree),
            start: i * 0.04,
            duration: 0.18,
            gain: 0.06,
          },
        ],
        at,
      );
    });
  }

  private noise(at: number, duration: number, gain: number, fromHz: number, toHz?: number): void {
    const length = Math.max(1, Math.floor(this.ctx.sampleRate * duration));
    const buffer = this.ctx.createBuffer(1, length, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i += 1) data[i] = Math.random() * 2 - 1;
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.Q.setValueAtTime(0.8, at);
    filter.frequency.setValueAtTime(fromHz, at);
    if (toHz !== undefined) filter.frequency.exponentialRampToValueAtTime(toHz, at + duration);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(gain, at + duration * 0.3);
    g.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    source.connect(filter);
    filter.connect(g);
    g.connect(this.destination);
    source.start(at);
    source.stop(at + duration + 0.02);
  }
}

/** Metronome and drum voices share the synth's building blocks. */
export class PercussionSynth {
  constructor(
    private readonly ctx: BaseAudioContext,
    private readonly destination: AudioNode,
  ) {}

  click(at: number, kind: 'accent' | 'normal' | 'subdivision'): void {
    const hz = kind === 'accent' ? 1200 : kind === 'normal' ? 880 : 600;
    const duration = kind === 'accent' ? 0.03 : 0.025;
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(hz, at);
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(kind === 'subdivision' ? 0.12 : 0.25, at);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    osc.connect(gain);
    gain.connect(this.destination);
    osc.start(at);
    osc.stop(at + duration + 0.01);
  }

  /** General MIDI drum numbers: 36 kick, 38 snare, 42 closed hat, 46 open hat. */
  drum(midi: number, at: number, velocity = 0.8): void {
    if (midi === 36) {
      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(150, at);
      osc.frequency.exponentialRampToValueAtTime(45, at + 0.12);
      const gain = this.ctx.createGain();
      gain.gain.setValueAtTime(velocity, at);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.25);
      osc.connect(gain);
      gain.connect(this.destination);
      osc.start(at);
      osc.stop(at + 0.3);
      return;
    }
    const isSnare = midi === 38 || midi === 40;
    const duration = isSnare ? 0.18 : midi === 46 ? 0.3 : 0.06;
    const length = Math.max(1, Math.floor(this.ctx.sampleRate * duration));
    const buffer = this.ctx.createBuffer(1, length, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i += 1) data[i] = Math.random() * 2 - 1;
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    const filter = this.ctx.createBiquadFilter();
    filter.type = isSnare ? 'bandpass' : 'highpass';
    filter.frequency.setValueAtTime(isSnare ? 1800 : 7000, at);
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(velocity * (isSnare ? 0.5 : 0.3), at);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.destination);
    source.start(at);
    source.stop(at + duration + 0.02);
    if (isSnare) {
      const osc = this.ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(180, at);
      const g = this.ctx.createGain();
      g.gain.setValueAtTime(velocity * 0.4, at);
      g.gain.exponentialRampToValueAtTime(0.0001, at + 0.08);
      osc.connect(g);
      g.connect(this.destination);
      osc.start(at);
      osc.stop(at + 0.1);
    }
  }
}
